import React from 'react';
import { Container, Row, Col, Tab, Nav } from 'react-bootstrap';

function IOSFeatures() {
  const tabContent = {
    details: {
      title: 'Comprehensive Dive Details',
      shortTitle: 'Comprehensive Dive Details',
      description: 'Analyze every aspect of your dives with detailed visualizations and sensor data.',
      features: [
        'Interactive dive profile charts',
        'GPS route mapping',
        'Sensor data graphs',
        'Export and sharing capabilities'
      ],
      icon: 'fas fa-info-circle',
      color: '#9C27B0'
    },
    dashboard: {
      title: 'Advanced Analytics Dashboard',
      shortTitle: 'Analytics Dashboard',
      description: 'Get deep insights into your diving patterns with comprehensive analytics, trends, and personal records tracking.',
      features: [
        'Period-to-period comparison metrics',
        'Interactive depth and duration charts',
        'Personal records with improvement tracking',
        'AI-driven insights and recommendations'
      ],
      icon: 'fas fa-chart-area',
      color: '#4a90e2'
    },
    tissue: {
      title: 'Tissue Loading Monitor',
      shortTitle: 'Tissue Loading',
      description: 'Professional-grade decompression tracking with real-time tissue saturation monitoring.',
      features: [
        'Full Bühlmann ZH-L16C implementation',
        'No-fly time calculations',
        'Maximum altitude restrictions',
        'Live Activities on Dynamic Island'
      ],
      icon: 'fas fa-lungs',
      color: '#f44336'
    },
    planning: {
      title: 'Professional Dive Planning Suite',
      shortTitle: 'Dive Planning',
      description: 'Comprehensive planning tools for safe and successful dives, from multi-day trips to technical gas calculations.',
      features: [
        'Multi-day trip planner with day-by-day scheduling',
        'Gas planning suite (SAC rates, Nitrox, Trimix)',
        'Equipment packing lists with 11 categories',
        'Interactive Bühlmann ZH-L16C & PADI RDP tables',
        'Weight calculator with salinity adjustments',
        'No-decompression limit optimization',
        'Surface interval calculator with pressure groups',
        'Save and reuse trip & equipment templates'
      ],
      icon: 'fas fa-route',
      color: '#FF9800'
    },
    import: {
      title: 'Import & Export System',
      shortTitle: 'Import & Export',
      description: 'Seamlessly manage your dive data across devices and platforms with powerful import/export capabilities.',
      features: [
        'HealthKit dive workout import',
        'JSON data export/import',
        'Manual Watch synchronization',
        'CloudKit backup and restore'
      ],
      icon: 'fas fa-exchange-alt',
      color: '#4CAF50'
    }
  };

  return (
    <section id="ios" className="section-padding bg-alt">
      <Container>
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="section-title">iOS Companion App</h2>
          <p className="section-subtitle">
            Powerful analytics and planning tools at your fingertips
          </p>
        </div>

        <Tab.Container defaultActiveKey="details">
          <Row>
            <Col lg={3}>
              <Nav variant="pills" className="flex-column" data-aos="fade-right">
                {Object.entries(tabContent).map(([key, content]) => (
                  <Nav.Item key={key} className="mb-3">
                    <Nav.Link
                      eventKey={key}
                      style={{
                        borderRadius: '15px',
                        padding: '1rem',
                        transition: 'all 0.3s ease',
                        border: '2px solid transparent'
                      }}
                      className="ios-nav-link"
                    >
                      <i className={content.icon} style={{ marginRight: '10px', color: content.color }}></i>
                      {content.shortTitle}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
            <Col lg={9}>
              <Tab.Content data-aos="fade-left">
                {Object.entries(tabContent).map(([key, content]) => (
                  <Tab.Pane eventKey={key} key={key}>
                    <div className="ios-tab-panel">
                      <div className="d-flex align-items-center mb-4">
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: `linear-gradient(135deg, ${content.color}dd, ${content.color}88)`,
                          border: '1px solid rgba(255,255,255,0.35)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)',
                          borderRadius: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '1.5rem'
                        }}>
                          <i className={content.icon} style={{ color: 'white', fontSize: '1.5rem' }}></i>
                        </div>
                        <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{content.title}</h3>
                      </div>
                      <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                        {content.description}
                      </p>
                      <Row>
                        {content.features.map((feature, index) => (
                          <Col md={6} key={index} className="mb-3">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <i className="fas fa-check-circle" style={{
                                color: content.color,
                                marginRight: '10px',
                                fontSize: '1.2rem'
                              }}></i>
                              <span>{feature}</span>
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <div style={{
                        marginTop: '2rem',
                        padding: '1.5rem',
                        background: `linear-gradient(135deg, ${content.color}11, ${content.color}22)`,
                        borderRadius: '15px',
                        border: `1px solid ${content.color}33`
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap'
                        }}>
                          <div>
                            <h5 style={{ color: content.color, marginBottom: '0.5rem' }}>
                              <i className="fas fa-mobile-alt" style={{ marginRight: '8px' }}></i>
                              iPhone & iPad Compatible
                            </h5>
                            <p style={{ margin: 0, opacity: 0.8 }}>
                              Optimized for all screen sizes with responsive design
                            </p>
                          </div>
                          <div style={{
                            background: content.color,
                            color: 'white',
                            padding: '0.5rem 1.5rem',
                            borderRadius: '25px',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}>
                            iOS 17.6+
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </section>
  );
}

export default IOSFeatures;